'use client';

/* eslint-disable react-refresh/only-export-components */

import * as React from 'react';
import { cn } from '@/lib/utils';

interface MoneyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode'> {
  /** Formatted Brazilian-currency string, e.g. "1.234,56". Empty string when no value. */
  value: string;
  /**
   * Called on every keystroke with the freshly formatted string and the parsed
   * numeric value. The string keeps the existing convention used across the
   * codebase ("1.234,56") so submission/parsing logic does not need to change.
   */
  onChange: (formatted: string, numeric: number) => void;
  /** Renders an "R$" prefix inside the input. Defaults to true. */
  showCurrencyPrefix?: boolean;
  /** Maximum value in cents. Defaults to 99_999_999_999 (≈ 1 bilhão de reais). */
  maxCents?: number;
}

function formatFromCents(cents: number): string {
  if (!cents) return '';
  const int = Math.floor(cents / 100);
  const dec = (cents % 100).toString().padStart(2, '0');
  const intStr = int.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${intStr},${dec}`;
}

function digitsToCents(raw: string, maxCents: number): number {
  const clean = raw.replace(/\D/g, '');
  if (!clean) return 0;
  // Cap input length to avoid Number overflow on huge pastes.
  const trimmed = clean.slice(0, 15);
  const cents = parseInt(trimmed, 10);
  if (Number.isNaN(cents)) return 0;
  return Math.min(cents, maxCents);
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, showCurrencyPrefix = true, maxCents = 99_999_999_999, className, placeholder, ...rest },
  ref,
) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cents = digitsToCents(event.target.value, maxCents);
    const formatted = formatFromCents(cents);
    const numeric = cents / 100;
    onChange(formatted, numeric);
  };

  // Keep the caret at the end after re-formatting — typing "1234" should show
  // "12,34" with the caret at the right edge, not jumping back to the prefix.
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  };
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (document.activeElement !== el) return;
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [value]);

  return (
    <div className="relative w-full">
      {showCurrencyPrefix && (
        <span
          className={cn(
            'pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 font-mono text-sm',
            value ? 'text-text-secondary' : 'text-text-tertiary',
          )}
        >
          R$
        </span>
      )}
      <input
        ref={setRefs}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={value}
        onChange={handleChange}
        placeholder={placeholder ?? '0,00'}
        className={cn(showCurrencyPrefix ? 'pl-12' : undefined, className)}
        {...rest}
      />
    </div>
  );
});

/**
 * Parse a BRL-formatted string like "1.234,56" back to a number.
 * Returns 0 for empty / unparseable input.
 */
export function parseMoneyInput(value: string): number {
  if (!value) return 0;
  const normalized = value.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}
